import React from 'react';
import { Globe, Truck, BarChart3, Smartphone, Clock, TrendingUp, Users, Award } from 'lucide-react';

const ForRetailers = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'Instant Online Presence',
      description: 'Transform your physical store into a digital storefront without technical expertise',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      icon: Truck,
      title: 'Built-in Logistics',
      description: 'Our delivery network handles everything from pickup to doorstep delivery',
      color: 'bg-green-50 text-green-600'
    },
    {
      icon: BarChart3,
      title: 'Fashion Insights',
      description: 'Understand trends, customer preferences, and styling patterns to curate collections smarter',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      icon: Smartphone,
      title: 'Easy-to-Use Tech',
      description: 'Simple dashboard to manage orders, inventory, and customer interactions',
      color: 'bg-coral-light text-coral'
    },
    {
      icon: Clock,
      title: 'Faster Sales Cycle',
      description: 'From order to delivery in minutes - increase order frequency and volume',
      color: 'bg-orange-50 text-orange-600'
    },
    {
      icon: TrendingUp,
      title: 'Grow Your Reach',
      description: 'Access customers beyond your physical location and expand market presence',
      color: 'bg-indigo-50 text-indigo-600'
    },
    {
      icon: Users,
      title: 'Customer Relationships',
      description: 'Build lasting connections with personalized service and recommendations',
      color: 'bg-pink-50 text-pink-600'
    },
    {
      icon: Award,
      title: 'Competitive Edge',
      description: 'Stay ahead of the curve in the quick commerce revolution',
      color: 'bg-teal-50 text-teal-600'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-2 bg-coral-light rounded-full">
              <span className="text-sm font-semibold text-coral">For Retailers & Merchants</span>
            </div>
          </div>
          
          {/* Heading */}
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight text-center mb-6">
            Your Store,
            <span className="block text-coral mt-2">Supercharged</span>
          </h2>
          
          {/* Description */}
          <p className="text-xl text-gray-600 leading-relaxed text-center mb-12 max-w-3xl mx-auto">
            Trendrush provides the complete technology and logistics infrastructure fashion retailers need 
            to compete in today's fast-paced market. Transform your apparel, footwear, or accessories store into a quick commerce powerhouse.
          </p>

          <div className="space-y-4 pt-4 max-w-3xl mx-auto">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 rounded-lg bg-coral flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">✓</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">No Upfront Investment</h3>
                <p className="text-gray-600">Start selling online without expensive tech setup or infrastructure</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 rounded-lg bg-coral flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">✓</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Reach Customers Fast</h3>
                <p className="text-gray-600">Get to customers quickly with quality service and build lasting brand loyalty</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="w-12 h-12 rounded-lg bg-coral flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">✓</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Actionable Insights</h3>
                <p className="text-gray-600">Data-driven recommendations to optimize your product mix</p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-20">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                className="group p-6 rounded-2xl bg-gray-50 border border-gray-100 hover:shadow-xl hover:border-coral transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-xl ${benefit.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-7 h-7" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            );
          })}
        </div>

        {/* CTA Box */}
        <div className="mt-16 bg-gradient-to-br from-coral to-coral/80 rounded-3xl p-12 text-center shadow-2xl">
          <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Retail Business?
          </h3>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join the retailers who are already winning with Trendrush's tech and logistics platform
          </p>
          <a
            href="#contact"
            className="inline-flex items-center px-8 py-4 bg-white text-coral rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all"
          >
            Contact Merchant Support
          </a>
        </div>
      </div>
    </section>
  );
};

export default ForRetailers;
